import {
    type AppointmentReminder,
    AppointmentTypeFilterOption,
} from "@accurx/api/appointment";
import { Ds } from "@accurx/design";

import { useMessageParts } from "../../hooks";
import { TemplateType } from "../../types";
import { PreviewCardAdminButtons } from "./components/PreviewCardAdminButtons";
import { PreviewCardNonAdminButtons } from "./components/PreviewCardNonAdminButtons";

export interface PreviewCardProps {
    reminder: AppointmentReminder;
    badgeText?: string;
    onEdit: (reminder: AppointmentReminder) => void;
    appointmentTypeOptions: AppointmentTypeFilterOption[];
    workspaceId: number;
    workspaceHasMultipleSites: boolean;
    workspaceName?: string;
    isAdminUser: boolean;
}

export const PreviewCard = ({
    reminder,
    badgeText,
    onEdit,
    appointmentTypeOptions,
    workspaceId,
    workspaceName,
    workspaceHasMultipleSites,
    isAdminUser,
}: PreviewCardProps) => {
    const headerText =
        reminder.slotTypes[0] +
        (reminder.slotTypes.length > 1
            ? ` (+ ${reminder.slotTypes.length - 1} more)`
            : "");

    const { fullMessage } = useMessageParts({
        templateType: reminder.type as TemplateType,
        customMessage: reminder.customMessageBody || "",
        postAppointmentMessageBody: reminder.postAppointmentCustomMessage || "",
        workspaceHasMultipleSites,
        workspaceName: workspaceName || "",
    });

    return (
        <Ds.Box width="100%" data-testid="preview-card">
            <Ds.Card appearance="elevated">
                <Ds.Card.Header px="3" py="2">
                    <Ds.Flex justifyContent="space-between" alignItems="center">
                        <Ds.Flex flexDirection="column" alignItems="start">
                            <Ds.Text weight="bold">{headerText}</Ds.Text>
                            {badgeText && (
                                <Ds.Badge color="greyscale">
                                    {badgeText}
                                </Ds.Badge>
                            )}
                        </Ds.Flex>

                        <Ds.Flex
                            gap="2"
                            flexWrap={{ xs: "wrap", md: "nowrap" }}
                            justifyContent="flex-end"
                        >
                            {isAdminUser ? (
                                <>
                                    <PreviewCardAdminButtons
                                        workspaceId={workspaceId.toString()}
                                        reminder={reminder}
                                        onEdit={() => onEdit(reminder)}
                                    />
                                </>
                            ) : (
                                <PreviewCardNonAdminButtons
                                    reminder={reminder}
                                    appointmentTypeOptions={
                                        appointmentTypeOptions
                                    }
                                    workspaceHasMultipleSites={
                                        workspaceHasMultipleSites
                                    }
                                    workspaceName={workspaceName}
                                />
                            )}
                        </Ds.Flex>
                    </Ds.Flex>
                </Ds.Card.Header>
                <Ds.Card.Body p="3">
                    <Ds.Text>{fullMessage}</Ds.Text>
                </Ds.Card.Body>
            </Ds.Card>
        </Ds.Box>
    );
};
